/**
 * CombatScene.ts — Interactive combat UI scene.
 *
 * Manages the round-by-round combat loop with menu-driven player choices.
 * Combat is transient — results are applied to GameState when scene exits.
 * After combat, pops itself and lets StoryScene advance to victory/defeat node.
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import type { CombatAction, CombatResult, EnemyDefinition } from '@core/combat/CombatTypes';
import type { CombatSession } from '@core/systems/CombatSystem';
import type { HintTriggerType } from '@ui/HintManager';
import { CombatStatusBar } from '@ui/CombatStatusBar';
import { theme } from '@ui/ColorTheme';

export class CombatScene implements Scene {
  name = 'CombatScene';

  private enemyDef: EnemyDefinition;
  private onComplete: (result: CombatResult) => void;

  constructor(enemyDef: EnemyDefinition, onComplete: (result: CombatResult) => void) {
    this.enemyDef = enemyDef;
    this.onComplete = onComplete;
  }

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    if (!ctx.gameState) throw new Error('CombatScene requires GameState');

    const { menu, effects, gameState, frameRenderer, visualLayer, hintManager, terminalFrame } = ctx;
    const combatSystem = ctx.combatSystem;
    const combatStatusBar = new CombatStatusBar(ctx.terminal);

    // Switch to combat particles
    visualLayer.enterCombat();

    // Initialize combat
    const session = combatSystem.initCombat(gameState, this.enemyDef);

    // Update terminal frame
    terminalFrame.updateInfo({
      chapter: 'COMBAT',
      location: this.enemyDef.name,
    });

    // Show combat intro
    ctx.sprites.hide();
    const introLayout = frameRenderer.drawFrame({
      title: 'COMBAT',
      headerRight: this.enemyDef.isBoss ? '★ BOSS ★' : '',
      borderColor: 'red',
      headerColor: 'red',
    });

    // Show enemy sprite (viewport-aware height)
    const enemySprite = this.getEnemySprite(this.enemyDef.id);
    if (enemySprite) {
      try {
        const spriteHeight = ctx.viewport.getSpriteHeight();
        await ctx.sprites.drawInPanel(enemySprite, introLayout.content.startRow, introLayout.content.startCol, introLayout.content.width, spriteHeight);
      } catch (err) {
        console.warn(`Failed to load sprite for ${this.enemyDef.id}:`, err);
      }
    }

    const introWriter = frameRenderer.createWriter(introLayout.content, effects);
    const introOffset = introWriter.calculateVerticalOffset(8);
    introWriter.skip(introOffset);
    introWriter.blankLine();
    introWriter.writeCentered('════════════════════════════════════════', 'red');
    introWriter.blankLine();
    const bossStr = this.enemyDef.isBoss ? ' ★ BOSS ENCOUNTER ★' : '';
    introWriter.writeCentered(`COMBAT!${bossStr}`, 'red');
    introWriter.blankLine();
    introWriter.writeCentered(this.enemyDef.name, 'red');
    introWriter.writeLine(`  ${this.enemyDef.description}`, 'white');
    introWriter.blankLine();
    introWriter.writeCentered('════════════════════════════════════════', 'red');

    // Trigger combat hint
    await hintManager.maybeShowHint('first_combat' as HintTriggerType, introWriter);
    if (this.enemyDef.isBoss) {
      await hintManager.maybeShowHint('first_boss' as HintTriggerType, introWriter);
    }

    await introWriter.pressAnyKey();

    // Combat loop
    while (true) {
      // Draw combat frame
      ctx.sprites.hide();
      const frostbite = gameState.character.frostbite +
        session.roundHistory.reduce((sum, r) => sum + r.playerFrostbiteGain, 0);
      const footer = `HP: ${session.player.hp}/${session.player.hpMax}  |  Frost: ${frostbite}/100  |  Round ${session.round + 1}`;

      const layout = frameRenderer.drawFrame({
        title: `COMBAT — Round ${session.round + 1}`,
        headerRight: this.enemyDef.name,
        footer,
        borderColor: 'red',
      });

      // Show enemy sprite (viewport-aware height)
      const enemySprite = this.getEnemySprite(this.enemyDef.id);
      if (enemySprite) {
        try {
          const spriteHeight = ctx.viewport.getSpriteHeight();
          await ctx.sprites.drawInPanel(enemySprite, layout.content.startRow, layout.content.startCol, layout.content.width, spriteHeight);
        } catch (err) {
          console.warn(`Failed to load sprite for ${this.enemyDef.id}:`, err);
        }
      }

      // Render combat HUD within frame
      const writer = frameRenderer.createWriter(layout.content, effects);
      this.renderCombatHUD(writer, session, combatStatusBar, gameState);
      writer.blankLine();

      // Build action menu
      const options = this.buildActionMenu(session);
      const menuCfg = writer.menuConfig();

      const sel = await menu.show(options, {
        showExit: false,
        startRow: menuCfg.startRow,
        startCol: menuCfg.startCol,
        maxWidth: menuCfg.maxWidth,
        onBeforeRender: () => {
          ctx.sprites.hide();
          frameRenderer.drawFrame({
            title: `COMBAT — Round ${session.round + 1}`,
            headerRight: this.enemyDef.name,
            footer,
            borderColor: 'red',
          });
          const w = frameRenderer.createWriter(layout.content, effects);
          this.renderCombatHUD(w, session, combatStatusBar, gameState);
        },
      });

      if (sel < 0 || sel >= options.length) continue;

      const action = this.selectionToAction(sel, session);
      if (!action) continue;

      // Handle flee specially
      if (action.type === 'flee') {
        const { fled, roundResult } = combatSystem.attemptFlee(session);
        this.showRoundResult(writer, roundResult);
        await writer.pressAnyKey();

        if (fled) {
          const outcome = combatSystem.checkEnd(session, true)!;
          const result = combatSystem.finalizeCombat(session, outcome, gameState);
          this.onComplete(result);
          visualLayer.exitCombat();
          await engine.popScene();
          return;
        }
      } else {
        const roundResult = combatSystem.executeRound(session, action);
        this.showRoundResult(writer, roundResult);
        await writer.pressAnyKey();
      }

      // Check combat end
      const outcome = combatSystem.checkEnd(session);
      if (outcome) {
        const result = combatSystem.finalizeCombat(session, outcome, gameState);
        await this.showCombatEnd(engine, result);
        this.onComplete(result);
        visualLayer.exitCombat();
        await engine.popScene();
        return;
      }
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  // ─── Combat HUD ──────────────────────────────────────────

  private renderCombatHUD(
    writer: import('@ui/PanelWriter').PanelWriter,
    session: CombatSession,
    _combatStatusBar: CombatStatusBar,
    gameState: import('@core/engine/GameState').GameState,
  ): void {
    const frostbite = gameState.character.frostbite +
      session.roundHistory.reduce((sum, r) => sum + r.playerFrostbiteGain, 0);

    // Player HP bar
    const pHpPct = Math.max(0, session.player.hp / session.player.hpMax);
    const pBarLen = 20;
    const pFilled = Math.round(pHpPct * pBarLen);
    const pBar = '█'.repeat(pFilled) + '░'.repeat(pBarLen - pFilled);
    writer.writeLine(`  You  [${pBar}] ${session.player.hp}/${session.player.hpMax} HP`, 'green');

    // Enemy HP bar
    const eHpPct = Math.max(0, session.enemy.hp / session.enemy.hpMax);
    const eFilled = Math.round(eHpPct * pBarLen);
    const eBar = '█'.repeat(eFilled) + '░'.repeat(pBarLen - eFilled);
    writer.writeLine(`  ${session.enemyDef.name}  [${eBar}] ${session.enemy.hp}/${session.enemy.hpMax} HP`, 'red');

    writer.blankLine();
    writer.writeLine(`  Frostbite: ${frostbite}/100`, 'frostbiteCyan');
  }

  // ─── Action Menu ──────────────────────────────────────────

  private buildActionMenu(session: CombatSession): MenuOption[] {
    const options: MenuOption[] = [
      { label: 'Attack', color: 'red' },
      { label: 'Defend', color: 'frostBlue' },
    ];

    for (const ability of session.abilities) {
      const canAfford = ability.frostbiteCost === 0 ||
        (session.startFrostbite + session.roundHistory.reduce((s, r) => s + r.playerFrostbiteGain, 0) + ability.frostbiteCost) < 100;
      options.push({
        label: `${ability.name}`,
        color: canAfford ? 'magenta' : 'charcoal',
        suffix: ability.frostbiteCost > 0 ? `[Frost ${ability.frostbiteCost}]` : '',
        suffixColor: 'frostbiteCyan',
        disabled: !canAfford,
      });
    }

    options.push({ label: 'Flee', color: 'amber' });

    return options;
  }

  private selectionToAction(sel: number, session: CombatSession): CombatAction | null {
    if (sel === 0) return { type: 'attack' };
    if (sel === 1) return { type: 'defend' };

    const abilityIndex = sel - 2;
    if (abilityIndex >= 0 && abilityIndex < session.abilities.length) {
      const ability = session.abilities[abilityIndex];
      return { type: 'ability', targetId: ability.id };
    }

    if (sel === 2 + session.abilities.length) return { type: 'flee' };

    return null;
  }

  // ─── Round Display ────────────────────────────────────────

  private showRoundResult(writer: import('@ui/PanelWriter').PanelWriter, result: any): void {
    writer.blankLine();
    writer.writeLine('  ────────────────────────────────', 'frostSilver');
    writer.writeLine(`  ${result.narrative}`, 'white');

    if (result.playerDamageDealt > 0) {
      writer.writeLine(`  ${theme.getSymbolPrefix('green')}You dealt ${result.playerDamageDealt} damage`, 'green');
    }
    if (result.playerDamageTaken > 0) {
      writer.writeLine(`  ${theme.getSymbolPrefix('red')}You took ${result.playerDamageTaken} damage`, 'red');
    }
    if (result.playerFrostbiteGain > 0) {
      writer.writeLine(`  ${theme.getSymbolPrefix('frostbiteCyan')}+${result.playerFrostbiteGain} frostbite`, 'frostbiteCyan');
    }
    if (result.abilityUsed) {
      writer.writeLine(`  ${theme.getSymbolPrefix('magenta')}Used: ${result.abilityUsed}`, 'magenta');
    }
    writer.writeLine('  ────────────────────────────────', 'frostSilver');
  }

  // ─── Combat End Display ───────────────────────────────────

  private async showCombatEnd(engine: GameEngine, result: CombatResult): Promise<void> {
    const { frameRenderer, effects } = engine.getContext();
    const ctx = engine.getContext();

    ctx.sprites.hide();

    const outcomeColor = result.outcome === 'victory' ? 'green'
      : result.outcome === 'defeat' ? 'red'
      : 'yellow';

    const outcomeTitle = result.outcome === 'victory' ? 'VICTORY!'
      : result.outcome === 'defeat' ? 'DEFEAT'
      : 'ESCAPED';

    const layout = frameRenderer.drawFrame({
      title: outcomeTitle,
      borderColor: outcomeColor,
      headerColor: outcomeColor,
    });

    const writer = frameRenderer.createWriter(layout.content, effects);
    const offset = writer.calculateVerticalOffset(10);
    writer.skip(offset);

    writer.writeCentered('════════════════════════════════════════', outcomeColor);
    writer.writeCentered(outcomeTitle, 'icyWhite');
    writer.writeCentered('════════════════════════════════════════', outcomeColor);
    writer.blankLine();

    if (result.outcome === 'victory') {
      writer.writeLine(`  ${result.enemyName} defeated in ${result.rounds} rounds.`, 'white');
      writer.blankLine();
      if (result.xpEarned > 0) {
        writer.writeLine(`  ${theme.getSymbolPrefix('green')}+${result.xpEarned} XP`, 'green');
      }
      if (result.goldEarned > 0) {
        writer.writeLine(`  ${theme.getSymbolPrefix('yellow')}+${result.goldEarned} Gold`, 'yellow');
      }
      for (const loot of result.lootDropped) {
        writer.writeLine(`  ${theme.getSymbolPrefix('magenta')}Loot: ${loot}`, 'magenta');
      }
    } else if (result.outcome === 'defeat') {
      writer.writeLine(`  You have fallen to ${result.enemyName}.`, 'white');
    } else {
      writer.writeLine(`  You fled from ${result.enemyName} after ${result.rounds} rounds.`, 'white');
    }

    if (result.hpLost > 0) {
      writer.blankLine();
      writer.writeLine(`  HP lost: ${result.hpLost}`, 'red');
    }
    if (result.frostbiteGained > 0) {
      writer.writeLine(`  Frostbite gained: ${result.frostbiteGained}`, 'frostbiteCyan');
    }

    writer.blankLine();
    await writer.pressAnyKey();
  }

  // ─── Enemy Sprite Mapping ────────────────────────────────

  private getEnemySprite(enemyId: string): string | null {
    // Map enemy IDs to sprite keys
    const mapping: Record<string, string> = {
      // Undead warriors
      'skeletal_warrior': 'undead_warrior',
      'bone_knight': 'undead_warrior',
      'death_knight': 'undead_warrior',
      'wight_soldier': 'undead_warrior',

      // Frost creatures
      'frost_wolf': 'frost_wyrm',
      'ice_wraith': 'frost_wyrm',
      'frost_abomination': 'frost_wyrm',

      // Shadow/dark enemies
      'dark_acolyte': 'shadow_lord',
      'shadow_beast': 'shadow_lord',
      'shadow_commander': 'shadow_lord',
      'corrupted_guard': 'shadow_lord',
      'corrupted_captain': 'shadow_lord',
      'corrupted_mage': 'shadow_lord',
      'necromantic_horror': 'shadow_lord',
      'lich_servant': 'shadow_lord',

      // Bosses
      'boss_valdris': 'shadow_lord',
      'boss_necromancer_general': 'shadow_lord',
      'boss_bone_lord': 'ancient_guardian',
      'boss_lich_king': 'lich_king',
    };

    return mapping[enemyId] ?? null;
  }
}
